// Normalisation of the TipTap `doc` JSON that dashboard replies arrive in,
// applied before it is turned into HTML for a chat bubble.
//
// The dashboard composer leaves blank paragraphs in the stored document that
// the author never typed: the editor starts on an empty paragraph, and the
// TrailingNode extension appends one after every trailing block node so there
// is always somewhere to click below an image. Nobody sees them in the
// dashboard — an empty `<p>` has no line box, so it collapses to zero height.
//
// The widget can't ignore them: `.message-text p:empty:before` injects a space
// so that blank lines a user typed BETWEEN paragraphs survive, which gives the
// phantom paragraphs a full line box (~20px) plus their 8px margin each. Around
// an image that reads as a big dead band above and below the picture (#143113).
//
// So we drop the phantom ones — at the edges of the message, or pressed against
// a media block — and keep every blank line that sits between two paragraphs of
// real prose, which is the only place the author could have meant one.

export interface TipTapNode {
  type?: string;
  text?: string;
  content?: TipTapNode[];
  [key: string]: any;
}

// Block nodes that carry their own vertical rhythm (margins/padding), so a
// blank line next to them is always editor noise rather than authored spacing.
const MEDIA_NODE_TYPES = ["image", "imageBlock", "youtube", "iframe", "video"];

const isMediaNode = (node?: TipTapNode) =>
  !!node?.type && MEDIA_NODE_TYPES.includes(node.type);

// Empty, or holding nothing but whitespace text — both render as a blank line.
// A paragraph with a hardBreak in it is left alone: that break was typed.
const isBlankParagraph = (node?: TipTapNode) => {
  if (node?.type !== "paragraph") {
    return false;
  }

  const content = node.content;
  if (!content || content.length === 0) {
    return true;
  }

  return content.every(
    (child) => child?.type === "text" && !String(child.text ?? "").trim(),
  );
};

export const stripEditorBlankParagraphs = <T>(doc: T): T => {
  const node = doc as TipTapNode | null;
  const content = node?.content;

  if (!Array.isArray(content)) {
    return doc;
  }

  const kept = content.filter((child, index) => {
    if (!isBlankParagraph(child)) {
      return true;
    }

    // Leading / trailing blank lines: the composer's own scaffolding. A bubble
    // gets its breathing room from its padding, never from an empty line.
    if (index === 0 || index === content.length - 1) {
      return false;
    }

    return !isMediaNode(content[index - 1]) && !isMediaNode(content[index + 1]);
  });

  if (kept.length === content.length) {
    return doc;
  }

  return { ...node, content: kept } as T;
};
